import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { environment } from '../../config/environment';
import { apiRequest } from '../../services/apiClient';
import createSidebarSections from '../../config/sidebarSections';
import DashboardLayout from '../../components/DashboardLayout';
import './index.css';

const JOB_STATUS = {
  ACTIVE: '1',
  INACTIVE: '2',
  DELETED: '9',
};

const JobOpeningsPage = () => {
  const navigate = useNavigate();
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [jobOpeningsData, setJobOpeningsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewJob, setViewJob] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isViewLoading, setIsViewLoading] = useState(false);

  const userName = useMemo(() => {
    const userStr = localStorage.getItem('authUser') || sessionStorage.getItem('authUser');
    if (!userStr) return 'Rakesh';

    try {
      const user = JSON.parse(userStr);
      return user?.name || user?.fullName || user?.email || 'Rakesh';
    } catch {
      return 'Rakesh';
    }
  }, []);

  const getStatusLabel = (status) => {
    const normalized = String(status);
    if (normalized === JOB_STATUS.ACTIVE) return 'Active';
    if (normalized === JOB_STATUS.INACTIVE) return 'Inactive';
    return 'Inactive';
  };

  const getStatusClassName = (status) => {
    const normalized = String(status);
    if (normalized === JOB_STATUS.ACTIVE) return 'status-active';
    if (normalized === JOB_STATUS.INACTIVE) return 'status-closed';
    return 'status-closed';
  };

  const getSalaryFrequencyLabel = (value) => {
    const frequencyMap = {
      '1': 'Hourly',
      '2': 'Daily',
      '3': 'Weekly',
      '4': 'Monthly',
      '5': 'Yearly',
    };
    return frequencyMap[String(value)] || 'Monthly';
  };

  // ✅ Fetch Job Openings
  const fetchJobOpenings = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      const { ok, data } = await apiRequest(environment.endpoints.jobApplicationGetAll, {
        method: 'POST',
      });

      if (!ok || data?.response !== true) {
        throw new Error(data?.message || 'Unable to fetch job openings.');
      }

      const jobsList = Array.isArray(data?.data) ? data.data : [];
      
      // ✅ Fetch application count for each job
      const jobsWithCount = await Promise.all(
        jobsList.map(async (job) => {
          try {
            const countResponse = await apiRequest(environment.endpoints.getJobApplicationCount, {
              method: 'POST',
              body: { job_id: job.id },
            });
            
            if (countResponse.ok && countResponse.data?.response === true) {
              return { ...job, application_count: countResponse.data?.data?.count || 0 };
            }
            return { ...job, application_count: 0 };
          } catch {
            return { ...job, application_count: 0 };
          }
        })
      );
      
      setJobOpeningsData(jobsWithCount);
    } catch (error) {
      setJobOpeningsData([]);
      setError(error?.message || 'Unable to fetch job openings.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobOpenings();
  }, [fetchJobOpenings]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isViewModalOpen) {
        closeViewModal();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isViewModalOpen]);

  const filteredData = useMemo(() => {
    return jobOpeningsData.filter((job) =>
      (job.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (job.designation?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (job.job_type?.name || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [jobOpeningsData, searchTerm]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + itemsPerPage);

  const handleLogout = async () => {
    localStorage.removeItem('authUser');
    sessionStorage.removeItem('authUser');
    navigate('/login', { replace: true });
  };

  const sections = useMemo(
    () => createSidebarSections(navigate, 'job-openings'),
    [navigate]
  );

  const handleAddNewJob = () => {
    navigate('/job-openings/create');
  };

  const handleEdit = (jobId) => {
    navigate(`/job-openings/edit/${jobId}`);
  };

  const closeViewModal = () => {
    setIsViewModalOpen(false);
    setTimeout(() => setViewJob(null), 300);
  };

  const handleView = async (jobId) => {
    setIsViewModalOpen(true);
    setIsViewLoading(true);
    setViewJob(null);

    try {
      const { ok, data } = await apiRequest(`${environment.endpoints.jobApplicationGetOne}/${jobId}`, {
        method: 'POST',
      });

      if (!ok || data?.response !== true) {
        throw new Error(data?.message || 'Unable to fetch job details.');
      }

      setViewJob(data?.data || null);
    } catch (error) {
      closeViewModal();
      await Swal.fire({
        icon: 'error',
        text: error?.message || 'Unable to fetch job details.',
        confirmButtonColor: '#c7374e',
      });
    } finally {
      setIsViewLoading(false);
    }
  };

  const handleDelete = async (jobId) => {
    const confirmResult = await Swal.fire({
      icon: 'warning',
      title: 'Delete Job Opening?',
      text: 'This will permanently delete the selected job opening.',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#c7374e',
      cancelButtonColor: '#6b7280',
    });

    if (!confirmResult.isConfirmed) return;

    try {
      const { ok, data } = await apiRequest(`${environment.endpoints.jobApplicationDelete}/${jobId}`, {
        method: 'POST',
      });

      if (!ok || data?.response !== true) {
        await Swal.fire({
          icon: 'error',
          text: data?.message || 'Unable to delete job opening.',
          confirmButtonColor: '#c7374e',
        });
        return;
      }

      await Swal.fire({
        icon: 'success',
        text: data?.message || 'Job opening deleted successfully.',
        confirmButtonColor: '#0f7a8f',
      });

      fetchJobOpenings();
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        text: error?.message || 'Unable to delete job opening.',
        confirmButtonColor: '#c7374e',
      });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <DashboardLayout
      displayName={userName}
      pageTitle="Job Openings"
      dashboard={{
        active: false,
        onClick: () => navigate('/welcome'),
      }}
      sections={sections}
      onLogout={handleLogout}
    >
      <section className="job-openings-container">

        <div className="job-controls">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search jobs..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="search-input"
            />
            <span className="search-icon">🔍</span>
          </div>

          <div className="controls-right">
            <button className="btn-add-job float-end" onClick={handleAddNewJob}>
              ➕ Add New Job
            </button>
          </div>
        </div>

        {isLoading && (
          <div className="loading-status">
            <span className="loading-spinner"></span> Loading...
          </div>
        )}

        {!isLoading && error && (
          <div className="error-status">{error}</div>
        )}

        {!isLoading && !error && (
          <div className="table-wrapper">
            <table className="jobs-table">
              <thead>
                <tr>
                  <th className="col-sno">S.No</th>
                  <th className="col-title">Job Title</th>
                  <th className="col-description">Description</th>
                  <th className="col-openings">Openings</th>
                  <th className="col-designation">Designation</th>
                  <th className="col-salary">Salary</th>
                  <th className="col-jobtype">Job Type</th>
                  <th className="col-education">Education</th>
                  <th className="col-applications">Applications</th>
                  <th className="col-status">Status</th>
                  <th className="col-action">Action</th>
                </tr>
              </thead>
              <tbody>
                
                {paginatedData.length > 0 ? (
                  paginatedData.map((job, index) => (
                    
                    <tr key={job.id} className="table-row">
                      <td className="cell-sno">{startIndex + index + 1}</td>
                      <td className="cell-title" title={job.title || ''}>
                        {job.title || '-'}
                      </td>
                      <td className="cell-description" title={job.description || ''}>
                        {job.description
                          ? job.description.length > 80
                            ? job.description.substring(0, 80) + '...'
                            : job.description
                          : '-'}
                      </td>
                      {console.log('Job Data:', job)}
{console.log('Designation:', job.designation)}
{console.log('Designation Name:', job.designation?.name)}
                      <td className="cell-openings">
                        <span className="badge-openings">{job.openings || 0}</span>
                      </td>
                      <td className="cell-designation">{job.designation?.name || '-'}</td>
                      <td className="cell-salary">/.EN
                        ₹{job.salary_min || 0} - ₹{job.salary_max || 0} / {getSalaryFrequencyLabel(job.salary_frequency)}
                      </td>
                      <td className="cell-jobtype">
                        <span className="badge-jobtype">{job.job_type?.name || '-'}</span>
                      </td>
                      <td className="cell-education">
                        <span className="education-label">{job.education?.name || '-'}</span>
                      </td>
                      <td className="cell-applications">
                        <span className="badge-applications">
                          {job.application_count || 0}
                        </span>
                      </td>
                      <td className="cell-status">
                        <span className={`badge-status ${getStatusClassName(job.status)}`}>
                          {getStatusLabel(job.status)}
                        </span>
                      </td>
                      <td className="cell-action">
                        <div className="action-buttons">
                          <button
                            className="action-btn view-btn"
                            onClick={() => handleView(job.id)}
                            title="View"
                          >
                            👁
                          </button>
                          <button
                            className="action-btn edit-btn"
                            onClick={() => handleEdit(job.id)}
                            title="Edit"
                          >
                            ✏
                          </button>
                          <button
                            className="action-btn delete-btn"
                            onClick={() => handleDelete(job.id)}
                            title="Delete"
                          >
                            🗑
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="11" className="no-data">
                      No job openings found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="pagination-container">
          <div className="items-per-page">
            <label>Items per page:</label>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="items-select"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>

          <div className="pagination-info">
            {filteredData.length === 0 ? (
              <span>No results</span>
            ) : (
              <span>
                {startIndex + 1} – {Math.min(startIndex + itemsPerPage, filteredData.length)} of{' '}
                {filteredData.length}
              </span>
            )}
          </div>

          <div className="pagination-buttons">
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              ←
            </button>
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
            >
              →
            </button>
          </div>
        </div>
      </section>

      {isViewModalOpen && (
        <div className="view-modal-overlay" onClick={closeViewModal} role="presentation">
          <div className={`view-modal-container ${isViewModalOpen ? 'modal-drop-in' : ''}`} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="view-modal-header">
              <h3>Job Opening Details</h3>
              <button className="view-modal-close" onClick={closeViewModal}>×</button>
            </div>
            <div className="view-modal-body">
              {isViewLoading ? (
                <div className="view-modal-loading">
                  <span className="loading-spinner"></span> Loading details...
                </div>
              ) : viewJob ? (
                <div className="view-details-grid">
                  <div className="view-detail-item">
                    <span className="view-detail-label">Job Title</span>
                    <span className="view-detail-value">{viewJob.title || '-'}</span>
                  </div>
                  <div className="view-detail-item">
                    <span className="view-detail-label">Job Type</span>
                    <span className="view-detail-value">{viewJob.job_type?.name || '-'}</span>
                  </div>
                  <div className="view-detail-item">
                    <span className="view-detail-label">Designation</span>
                    <span className="view-detail-value">{viewJob.designation?.name || '-'}</span>
                  </div>
                  <div className="view-detail-item">
                    <span className="view-detail-label">Education</span>
                    <span className="view-detail-value">{viewJob.education?.name || '-'}</span>
                  </div>
                  <div className="view-detail-item" style={{ gridColumn: '1 / -1' }}>
                    <span className="view-detail-label">Job Description</span>
                    <span className="view-detail-value">{viewJob.description || '-'}</span>
                  </div>
                  <div className="view-detail-item">
                    <span className="view-detail-label">Skills</span>
                    <span className="view-detail-value">
                      {Array.isArray(viewJob.skills) && viewJob.skills.length > 0
                        ? viewJob.skills.join(', ')
                        : '-'}
                    </span>
                  </div>
                  <div className="view-detail-item">
                    <span className="view-detail-label">Experience</span>
                    <span className="view-detail-value">{viewJob.experience || '-'}</span>
                  </div>
                  <div className="view-detail-item">
                    <span className="view-detail-label">Openings</span>
                    <span className="view-detail-value">{viewJob.openings || 0}</span>
                  </div>
                  <div className="view-detail-item">
                    <span className="view-detail-label">Salary</span>
                    <span className="view-detail-value">
                      ₹{viewJob.salary_min || 0} - ₹{viewJob.salary_max || 0} / {getSalaryFrequencyLabel(viewJob.salary_frequency)}
                    </span>
                  </div>
                  <div className="view-detail-item">
                    <span className="view-detail-label">Location</span>
                    <span className="view-detail-value">{viewJob.location || '-'}</span>
                  </div>
                  <div className="view-detail-item">
                    <span className="view-detail-label">Deadline</span>
                    <span className="view-detail-value">{formatDate(viewJob.deadline_date_time)}</span>
                  </div>
                  <div className="view-detail-item">
                    <span className="view-detail-label">Recruiter Name</span>
                    <span className="view-detail-value">{viewJob.recruiter_name || '-'}</span>
                  </div>
                  <div className="view-detail-item">
                    <span className="view-detail-label">Recruiter Contact</span>
                    <span className="view-detail-value">{viewJob.recruiter_contact || '-'}</span>
                  </div>
                  <div className="view-detail-item">
                    <span className="view-detail-label">Recruiter Email</span>
                    <span className="view-detail-value">{viewJob.recruiter_email || '-'}</span>
                  </div>
                  <div className="view-detail-item">
                    <span className="view-detail-label">Status</span>
                    <span className="view-detail-value">
                      <span className={`badge-status ${getStatusClassName(viewJob.status)}`}>
                        {getStatusLabel(viewJob.status)}
                      </span>
                    </span>
                  </div>
                  <div className="view-detail-item">
                    <span className="view-detail-label">Sequence</span>
                    <span className="view-detail-value">{viewJob.sequence || '-'}</span>
                  </div>
                  <div className="view-detail-item">
                    <span className="view-detail-label">Created By</span>
                    <span className="view-detail-value">{viewJob.created_by?.name || '-'}</span>
                  </div>
                  <div className="view-detail-item">
                    <span className="view-detail-label">Created At</span>
                    <span className="view-detail-value">{formatDate(viewJob.created_at)}</span>
                  </div>
                  <div className="view-detail-item">
                    <span className="view-detail-label">Updated By</span>
                    <span className="view-detail-value">{viewJob.updated_by?.name || '-'}</span>
                  </div>
                  <div className="view-detail-item">
                    <span className="view-detail-label">Updated At</span>
                    <span className="view-detail-value">{viewJob.updated_at ? formatDate(viewJob.updated_at) : '-'}</span>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default JobOpeningsPage;